import React, { useState, useEffect, useCallback } from 'react'
import { useUser } from 'cor-business'
import styles from './index.module.scss'
import Modal from '../Modal'
import DropDown from '../DropDown'
import CloseIcon from '../../assets/icon/CloseIcon'
import CheckIcon from '../../assets/icon/CheckIcon'
import CheckboxActiveIcon from '../../assets/icon/CheckboxActiveIcon'
import CheckboxIcon from '../../assets/icon/CheckboxIcon'
import WorkspaceMembersService from '../../services/workspace-members.service'
import UserService from '../../services/user.service'
import { useWorkspace } from '../../context/workspace.context'
import { extractList, getWorkspaceMemberUserId } from '../../utils/members'
import { canAssignRole, filterAssignableRoles } from '../../utils/rolePermissions'
import WorkspaceModal from '../Modal/components/WorkspaceModal'
import WorkspaceRequiredModal from '../WorkspaceRequiredModal'

interface UserOption {
    id: string
    email: string
    first_name: string
    last_name: string
    avatar?: string
}

interface AddMemberModalProps {
    isOpen: boolean
    onClose: () => void
    // Called after existing users are added to the workspace, with the created
    // member records so callers (e.g. Participants) can update their list.
    onMembersAdded?: (members: any[]) => void
}

type Tab = 'existing' | 'new'

const initialInviteForm = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    role: '',
}

const ChevronDownIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 6L8 10L12 6" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const getUserDisplayName = (user: UserOption) => {
    if (user.first_name || user.last_name) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim()
    }

    return user.email
}

const getUserInitials = (user: Pick<UserOption, 'first_name' | 'last_name' | 'email'>) => {
    const initials = `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()

    return initials || user.email?.[0]?.toUpperCase() || 'U'
}

export default function AddMemberModal({ isOpen, onClose, onMembersAdded }: AddMemberModalProps) {
    const { workspace } = useWorkspace()
    const { user } = useUser()
    const [tab, setTab] = useState<Tab>('existing')

    // --- Existing tab state ---
    const [users, setUsers] = useState<UserOption[]>([])
    const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([])
    // Кто уже состоит в пространстве — такие в списке помечены галочкой и не выбираются.
    const [workspaceMemberIds, setWorkspaceMemberIds] = useState<Set<string>>(new Set())
    const [memberSearch, setMemberSearch] = useState('')
    const [searchFocused, setSearchFocused] = useState(false)
    const [loadingUsers, setLoadingUsers] = useState(false)
    const [usersLoaded, setUsersLoaded] = useState(false)
    const [submittingUsers, setSubmittingUsers] = useState(false)
    const [addMemberError, setAddMemberError] = useState<string | null>(null)

    // --- New tab (invite) state ---
    const [inviteForm, setInviteForm] = useState(initialInviteForm)
    const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({})
    const [inviteLoading, setInviteLoading] = useState(false)
    const [roles, setRoles] = useState<string[]>([])
    const [showWorkspaceModal, setShowWorkspaceModal] = useState(false)

    const resetAll = useCallback(() => {
        setTab('existing')
        setUsers([])
        setSelectedUsers([])
        setMemberSearch('')
        setSearchFocused(false)
        setUsersLoaded(false)
        setAddMemberError(null)
        setInviteForm(initialInviteForm)
        setInviteErrors({})
        setRoles([])
        setShowWorkspaceModal(false)
    }, [])

    const handleClose = () => {
        onClose()
        resetAll()
    }

    // Load roles for the invite form when the modal opens.
    useEffect(() => {
        if (!isOpen || !workspace?.id) return

        setRoles([])
        UserService.getRoles()
            .then((data) => {
                const roleItems = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.roles) ? data.roles : []
                const roleNames = roleItems
                    .map((role: any) => role.name || role.title || role)
                    .filter((role: unknown): role is string => typeof role === 'string')
                const assignableRoles = filterAssignableRoles(roleNames, user)

                setRoles(assignableRoles)
                setInviteForm((prev) => (
                    prev.role && !assignableRoles.includes(prev.role)
                        ? { ...prev, role: '' }
                        : prev
                ))
            })
            .catch(() => setRoles([]))
    }, [isOpen, user, workspace?.id])

    // Текущие участники пространства: их нельзя добавить повторно.
    useEffect(() => {
        if (!isOpen || !workspace?.id) {
            setWorkspaceMemberIds(new Set())
            return
        }

        WorkspaceMembersService.getMembers(Number(workspace.id), { limit: 1000 })
            .then((data) => {
                const memberIds = new Set<string>(
                    extractList(data)
                        .map((member: any) => getWorkspaceMemberUserId(member))
                        .filter(Boolean),
                )

                setWorkspaceMemberIds(memberIds)
                // Если кого-то успели выбрать до загрузки списка — убираем из выбора.
                setSelectedUsers((prev) => prev.filter((user) => !memberIds.has(String(user.id))))
            })
            .catch((e) => {
                console.error('Failed to fetch workspace members:', e)
            })
    }, [isOpen, workspace?.id])

    // --- Existing tab logic ---

    const fetchUsers = async (searchQuery = '') => {
        setLoadingUsers(true)

        try {
            const data = await UserService.getUsers({ search: searchQuery || undefined, limit: 1000 })
            setUsers(Array.isArray(data) ? data : [])
            setUsersLoaded(true)
        } catch (e) {
            console.error('Failed to fetch users:', e)
            setUsers([])
        } finally {
            setLoadingUsers(false)
        }
    }

    const handleMemberSearchFocus = () => {
        setSearchFocused(true)

        if (!usersLoaded) {
            void fetchUsers('')
        }
    }

    const handleMemberSearchChange = async (value: string) => {
        setMemberSearch(value)
        await fetchUsers(value)
    }

    const toggleUser = (user: UserOption) => {
        setSelectedUsers((prev) => (
            prev.some((item) => item.id === user.id)
                ? prev.filter((item) => item.id !== user.id)
                : [...prev, user]
        ))
    }

    const filteredUsers = users.filter((user) => {
        if (!memberSearch) {
            return true
        }

        const displayName = getUserDisplayName(user).toLowerCase()
        const normalizedSearch = memberSearch.toLowerCase()

        return displayName.includes(normalizedSearch) || user.email?.toLowerCase().includes(normalizedSearch)
    })

    const showUsersDropdown = searchFocused && (filteredUsers.length > 0 || loadingUsers)

    const handleAddMembers = async () => {
        if (!workspace?.id || selectedUsers.length === 0 || submittingUsers) {
            return
        }

        setSubmittingUsers(true)
        setAddMemberError(null)

        try {
            const createdMembers = await Promise.all(
                selectedUsers.map((user) => WorkspaceMembersService.addMember({
                    user_id: user.id,
                    workspace_id: Number(workspace.id),
                    role_in_workspace: 'MEMBER',
                }))
            )

            onMembersAdded?.(createdMembers.map((member: any, index: number) => ({
                ...member,
                __selectedUser: selectedUsers[index],
            })))
            handleClose()
        } catch (e: any) {
            console.error('Failed to add workspace members:', e)
            setAddMemberError(e?.data?.detail || 'Не удалось добавить участников')
        } finally {
            setSubmittingUsers(false)
        }
    }

    // --- New tab (invite) logic ---

    const updateInviteField = (field: string, value: string) => {
        setInviteForm((prev) => ({ ...prev, [field]: value }))
        setInviteErrors((prev) => ({ ...prev, [field]: '' }))
    }

    const validateInviteForm = (): boolean => {
        const errors: Record<string, string> = {}

        if (!inviteForm.firstName.trim()) errors.firstName = 'Введите имя'
        if (!inviteForm.lastName.trim()) errors.lastName = 'Введите фамилию'
        if (!inviteForm.phoneNumber.trim()) {
            errors.phoneNumber = 'Введите номер телефона'
        } else if (inviteForm.phoneNumber.replace(/[^0-9]/g, '').length < 10) {
            errors.phoneNumber = 'Номер телефона должен содержать минимум 10 цифр'
        }
        if (!inviteForm.email.trim()) {
            errors.email = 'Введите email'
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteForm.email)) {
            errors.email = 'Введите корректный email'
        }
        if (!inviteForm.role) errors.role = 'Выберите роль'

        setInviteErrors(errors)
        return Object.keys(errors).length === 0
    }

    const handleInviteSubmit = async () => {
        if (!validateInviteForm()) return

        if (!canAssignRole(inviteForm.role, user)) {
            setInviteErrors((prev) => ({
                ...prev,
                role: 'Недостаточно прав для назначения этой роли',
            }))
            return
        }

        if (!workspace?.id) return

        setInviteLoading(true)
        try {
            await UserService.inviteUser({
                email: inviteForm.email,
                role: inviteForm.role,
                first_name: inviteForm.firstName,
                last_name: inviteForm.lastName,
                phone_number: inviteForm.phoneNumber,
                expires_in_days: 7,
                workspace_id: Number(workspace.id),
            })
            handleClose()
        } catch (e: any) {
            const status = e?.response?.status

            if (status === 409) {
                setInviteErrors({ email: 'Активное приглашение для этого email уже существует' })
            } else {
                setInviteErrors({ email: e?.data?.detail || 'Ошибка при отправке приглашения' })
            }
        } finally {
            setInviteLoading(false)
        }
    }

    if (showWorkspaceModal) {
        return <WorkspaceModal onClose={() => setShowWorkspaceModal(false)} />
    }

    const hasWorkspace = Boolean(workspace?.id)

    if (!hasWorkspace) {
        return (
            <WorkspaceRequiredModal
                isOpen={isOpen}
                feature="participants"
                onClose={handleClose}
                onCreateWorkspace={() => setShowWorkspaceModal(true)}
            />
        )
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={handleClose} width={446}>
            <div className={styles.addMember}>
                <div className={styles.addMember__close} onClick={handleClose}>
                    <CloseIcon />
                </div>

                <h3 className={styles.addMember__title}>Добавить участников</h3>
                <p className={styles.addMember__subtitle}>Выберите способ добавления участника</p>

                <div className={styles.addMember__tabs}>
                    <button
                        type="button"
                        className={`${styles.addMember__tab} ${tab === 'existing' ? styles.addMember__tabActive : ''}`}
                        onClick={() => setTab('existing')}
                    >
                        Существующий
                    </button>
                    <button
                        type="button"
                        className={`${styles.addMember__tab} ${tab === 'new' ? styles.addMember__tabActive : ''}`}
                        onClick={() => setTab('new')}
                    >
                        Новый
                    </button>
                </div>

                {tab === 'existing' ? (
                    <div className={`${styles.addMember__content} ${styles.addMember__contentExisting}`}>
                        <div className={styles.addMember__scroll}>
                            <div className={styles.addMember__field}>
                                <div
                                    className={`${styles.addMember__search} ${searchFocused ? styles.addMember__searchFocused : ''}`}
                                >
                                    <svg className={styles.addMember__searchIcon} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M4.99902 11.2922C4.99902 7.8171 7.81612 5 11.2912 5C14.7662 5 17.5833 7.8171 17.5833 11.2922C17.5833 12.7779 17.0685 14.1431 16.2075 15.2196L16.1635 15.2745L18.8148 17.9259C19.0605 18.1716 19.0605 18.57 18.8148 18.8157C18.5691 19.0614 18.1707 19.0614 17.925 18.8157L15.2736 16.1644L15.2187 16.2084C14.1422 17.0695 12.7769 17.5843 11.2912 17.5843C7.81612 17.5843 4.99902 14.7672 4.99902 11.2922ZM11.2912 6.25843C8.51113 6.25843 6.25746 8.51211 6.25746 11.2922C6.25746 14.0722 8.51113 16.3259 11.2912 16.3259C14.0712 16.3259 16.3249 14.0722 16.3249 11.2922C16.3249 8.51211 14.0712 6.25843 11.2912 6.25843Z" fill="currentColor"/>
                                    </svg>
                                    <input
                                        className={styles.addMember__searchInput}
                                        placeholder="Имя, фамилия или email"
                                        value={memberSearch}
                                        onChange={(e) => void handleMemberSearchChange(e.target.value)}
                                        onFocus={handleMemberSearchFocus}
                                        onBlur={() => window.setTimeout(() => setSearchFocused(false), 150)}
                                    />
                                    {showUsersDropdown && (
                                        <div className={styles.addMember__dropdown}>
                                            {loadingUsers ? (
                                                <div className={`${styles.addMember__dropdownItem} ${styles.addMember__dropdownItemMuted}`}>
                                                    Загрузка...
                                                </div>
                                            ) : filteredUsers.length === 0 ? (
                                                <div className={`${styles.addMember__dropdownItem} ${styles.addMember__dropdownItemMuted}`}>
                                                    Никого не найдено
                                                </div>
                                            ) : filteredUsers.map((user) => {
                                                const isAlreadyMember = workspaceMemberIds.has(String(user.id))

                                                return (
                                                    <div
                                                        key={user.id}
                                                        className={`${styles.addMember__dropdownItem} ${isAlreadyMember ? styles.addMember__dropdownItemMember : ''}`}
                                                        title={isAlreadyMember ? 'Уже в пространстве' : undefined}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault()

                                                            if (!isAlreadyMember) {
                                                                toggleUser(user)
                                                            }
                                                        }}
                                                    >
                                                        <div className={styles.addMember__dropdownItemLeft}>
                                                            {user.avatar ? (
                                                                <img
                                                                    className={styles.addMember__dropdownAvatar}
                                                                    src={user.avatar}
                                                                    alt={getUserDisplayName(user)}
                                                                />
                                                            ) : (
                                                                <div className={styles.addMember__dropdownAvatarPlaceholder}>
                                                                    {getUserInitials(user)}
                                                                </div>
                                                            )}
                                                            <span className={styles.addMember__dropdownName}>{getUserDisplayName(user)}</span>
                                                        </div>
                                                        {isAlreadyMember || selectedUsers.some((item) => item.id === user.id)
                                                            ? <CheckboxActiveIcon />
                                                            : <CheckboxIcon />
                                                        }
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedUsers.length > 0 && (
                                <div className={styles.addMember__chips}>
                                    {selectedUsers.map((user) => (
                                        <div key={user.id} className={styles.addMember__chip}>
                                            <span>{getUserDisplayName(user)}</span>
                                            <svg
                                                className={styles.addMember__chipRemove}
                                                onClick={() => toggleUser(user)}
                                                width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M12 4L4 12" stroke="#202026" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M4 4L12 12" stroke="#202026" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {addMemberError && (
                                <div className={styles.addMember__error}>{addMemberError}</div>
                            )}
                        </div>

                        <button
                            type="button"
                            className={`${styles.addMember__submit} ${selectedUsers.length === 0 || submittingUsers ? styles.addMember__submitDisabled : ''}`}
                            disabled={selectedUsers.length === 0 || submittingUsers}
                            onClick={handleAddMembers}
                        >
                            {submittingUsers ? 'Добавление...' : 'Добавить участников'}
                        </button>
                    </div>
                ) : (
                    <div className={`${styles.addMember__content} ${styles.addMember__contentInvite}`}>
                        <div className={styles.addMember__scroll}>
                            <div className={styles.addMember__fields}>
                                <div className={styles.addMember__formField}>
                                    <label className={styles.addMember__formLabel}>Имя <span>*</span></label>
                                    <input
                                        className={`${styles.addMember__input} ${inviteErrors.firstName ? styles.addMember__inputError : ''}`}
                                        placeholder="Введите имя участника"
                                        value={inviteForm.firstName}
                                        onChange={(e) => updateInviteField('firstName', e.target.value)}
                                    />
                                    {inviteErrors.firstName && <span className={styles.addMember__fieldError}>{inviteErrors.firstName}</span>}
                                </div>
                                <div className={styles.addMember__formField}>
                                    <label className={styles.addMember__formLabel}>Фамилия <span>*</span></label>
                                    <input
                                        className={`${styles.addMember__input} ${inviteErrors.lastName ? styles.addMember__inputError : ''}`}
                                        placeholder="Введите фамилию участника"
                                        value={inviteForm.lastName}
                                        onChange={(e) => updateInviteField('lastName', e.target.value)}
                                    />
                                    {inviteErrors.lastName && <span className={styles.addMember__fieldError}>{inviteErrors.lastName}</span>}
                                </div>
                                <div className={styles.addMember__formField}>
                                    <label className={styles.addMember__formLabel}>Номер телефона <span>*</span></label>
                                    <input
                                        className={`${styles.addMember__input} ${inviteErrors.phoneNumber ? styles.addMember__inputError : ''}`}
                                        placeholder="Введите телефон участника"
                                        type="tel"
                                        value={inviteForm.phoneNumber}
                                        onChange={(e) => updateInviteField('phoneNumber', e.target.value.replace(/[^0-9+]/g, ''))}
                                    />
                                    {inviteErrors.phoneNumber && <span className={styles.addMember__fieldError}>{inviteErrors.phoneNumber}</span>}
                                </div>
                                <div className={styles.addMember__formField}>
                                    <label className={styles.addMember__formLabel}>Email <span>*</span></label>
                                    <input
                                        className={`${styles.addMember__input} ${inviteErrors.email ? styles.addMember__inputError : ''}`}
                                        placeholder="Введите email участника"
                                        type="email"
                                        value={inviteForm.email}
                                        onChange={(e) => updateInviteField('email', e.target.value)}
                                    />
                                    {inviteErrors.email && <span className={styles.addMember__fieldError}>{inviteErrors.email}</span>}
                                </div>
                                <div className={styles.addMember__formField}>
                                    <label className={styles.addMember__formLabel}>Роль <span>*</span></label>
                                    <DropDown
                                        closeDropDownByPick
                                        btn={
                                            <div className={`${styles.addMember__select} ${inviteErrors.role ? styles.addMember__selectError : ''} ${inviteForm.role ? styles.addMember__selectActive : ''}`}>
                                                <span className={inviteForm.role ? styles.addMember__selectValue : styles.addMember__selectPlaceholder}>
                                                    {inviteForm.role || 'Выберите роль'}
                                                </span>
                                                <ChevronDownIcon />
                                            </div>
                                        }
                                    >
                                        <div className={styles.addMember__roleDropdown}>
                                            {roles.map((role) => (
                                                <div
                                                    className={styles.addMember__roleDropdownItem}
                                                    key={role}
                                                    onClick={() => updateInviteField('role', role)}
                                                >
                                                    <span>{role}</span>
                                                    {inviteForm.role === role && <CheckIcon />}
                                                </div>
                                            ))}
                                        </div>
                                    </DropDown>
                                    {inviteErrors.role && <span className={styles.addMember__fieldError}>{inviteErrors.role}</span>}
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`${styles.addMember__submit} ${inviteLoading ? styles.addMember__submitDisabled : ''}`}
                            onClick={handleInviteSubmit}
                            disabled={inviteLoading}
                        >
                            {inviteLoading ? 'Отправка...' : 'Отправить приглашение'}
                        </button>
                    </div>
                )}
            </div>
        </Modal>
    )
}
